import React from "react";
import { useIntl } from "react-intl";

import { updateCandidateDetailsAsAdminService } from "@app/services/candidate/updateCandidateDetailsAsAdminService";

import { useToast } from "@hooks/useToast";

import { CONSENT_STATUS } from "@utils/enum";

export const useConsentStatusCard = ({
  orderSummaryProps,
  onUpdateOrderSummaryProps,
}) => {
  const { messages } = useIntl();
  const { showErrorToast, showSuccessToast } = useToast();

  const [isConfirmDialogVisible, setIsConfirmDialogVisible] =
    React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleCloseConfirmDialog = () => {
    setIsConfirmDialogVisible(false);
  };

  const handleOpenConfirmDialog = () => {
    setIsConfirmDialogVisible(true);
  };

  const handleConfirmConsent = React.useCallback(async () => {
    try {
      setIsLoading(true);

      const {
        candidates: [{ candidate_id }],
      } = orderSummaryProps;
      const payload = {
        consent_status: CONSENT_STATUS.COMPLETED,
      };

      const { data: { candidate: nextCandidate = {} } = {} } =
        await updateCandidateDetailsAsAdminService(candidate_id, payload);

      onUpdateOrderSummaryProps(prev => {
        const nextCandidates = prev.candidates.map(candidate => {
          if (candidate.candidate_id === candidate_id) {
            return nextCandidate;
          }

          return candidate;
        });

        return { ...prev, candidates: nextCandidates };
      });

      showSuccessToast();
    } catch {
      showErrorToast(messages.exception_error_message);
    } finally {
      setIsLoading(false);

      handleCloseConfirmDialog();
    }
  }, [
    messages.exception_error_message,
    onUpdateOrderSummaryProps,
    orderSummaryProps,
    showErrorToast,
    showSuccessToast,
  ]);

  const orderDetails = React.useMemo(() => {
    if (!orderSummaryProps)
      return {
        orderId: "",
        candidateName: "",
        services: [],
      };

    const { order_id = "", order_items = [] } = orderSummaryProps;
    const [{ person_name }] = order_items;
    const services = order_items.map(({ name, case_id }) => {
      return {
        name,
        caseId: case_id,
      };
    });

    return {
      orderId: order_id,
      candidateName: person_name,
      services: services,
    };
  }, [orderSummaryProps]);

  return {
    orderDetails,
    isConfirmDialogVisible,
    isLoading,
    handleCloseConfirmDialog,
    handleOpenConfirmDialog,
    handleConfirmConsent,
  };
};
